import { useEffect, useRef, useState } from 'react';
import { EditorState } from '@codemirror/state';
import { EditorView, keymap, lineNumbers } from '@codemirror/view';
import { defaultKeymap, history, historyKeymap } from '@codemirror/commands';
import { bracketMatching } from '@codemirror/language';

export const useCodeMirror = (initialDoc, onChange) => {
  const editorRef = useRef(null);
  const viewRef = useRef(null);
  const [editorView, setEditorView] = useState(null);
  const onChangeRef = useRef(onChange);

  // Keep onChange ref updated
  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  useEffect(() => {
    if (!editorRef.current) return;

    const startState = EditorState.create({
      doc: initialDoc,
      extensions: [
        lineNumbers(),
        history(),
        bracketMatching(),
        keymap.of([...defaultKeymap, ...historyKeymap]),
        EditorView.updateListener.of((update) => {
          if (update.docChanged) {
            onChangeRef.current(update.state.doc.toString());
          }
        }),
        // Custom theme for screenplay with centered content
        EditorView.theme({
          '&': {
            fontSize: '12pt',
            fontFamily: 'Courier, monospace',
            height: '100%',
            backgroundColor: '#f8fafc'
          },
          '.cm-scroller': {
            overflow: 'auto',
            padding: '1rem'
          },
          '.cm-content': {
            maxWidth: '6in',
            margin: '0 auto',
            padding: '4rem 1in 4rem 1.5in',
            backgroundColor: 'white',
            boxShadow: '0 1px 3px 0 rgb(0 0 0 / 0.1)',
            minHeight: '100%'
          },
          '.cm-line': {
            padding: '0'
          },
        }),
      ],
    });

    const view = new EditorView({
      state: startState,
      parent: editorRef.current,
    });

    viewRef.current = view;
    setEditorView(view);

    return () => {
      if (viewRef.current) {
        viewRef.current.destroy();
        viewRef.current = null;
      }
    };
  }, [initialDoc]);

  return { editorRef, editorView };
};
